import React, { useEffect, useRef } from 'react'
import SearchInput from '../SearchInput/SearchInput'
import IconButton from '../Button/IconButton'
import MessageItem from '../MessageItem/MessageItem'
import { Order, OrderLine, OrderMessageThread } from '../../model'
import DefaultLoader from '../Loaders/DefaultLoader'
import StartThreadButton from '../StartThreadButton/StartThreadButton'
import { createMessageThread } from '../../services/api/messages'
import { useNavigate } from 'react-router-dom'

type OrderAction = 'startThread' | 'cancel' | 'viewDetails'

interface MessagesListProps {
    messageThreads: OrderMessageThread[]
    selectedThread: number | null
    setSearchString: (query: string) => void
    loading: boolean
    className?: string
    canSearch?: boolean
    onOrderDetail?: boolean
    actionButtons?: OrderAction[]
    currentOrder?: Order
}

const MessagesList: React.FunctionComponent<MessagesListProps> = ({
    messageThreads,
    selectedThread,
    setSearchString,
    loading,
    className = '',
    canSearch = false,
    onOrderDetail,
    actionButtons,
    currentOrder
}: MessagesListProps) => {
    const handleSearchChange = (query: string) => {
        setSearchString(query)
    }

    const navigate = useNavigate()

    // Ref to the selected thread DOM element
    const selectedRef = useRef<HTMLDivElement | null>(null)

    // Scroll into view when selectedThread changes or list loads
    useEffect(() => {
        if (selectedRef.current) {
            selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [selectedThread, messageThreads])

    const orderId = onOrderDetail ? currentOrder?.id : undefined
    const defaultOrderId = orderId || messageThreads?.[0]?.order?.id

    const createMessage = async (orderId: number) => {
        // setLoading(true)
        const params: any = {
            order: Number(defaultOrderId)
        }
        if (orderId !== defaultOrderId) {
            params['subOrder'] = orderId
        }
        try {
            const messageId = await createMessageThread(params)
            navigate(`?selectedThread=${messageId}`)
        } catch (e) {
            console.log(e)
        }

        // setLoading(false)
    }

    const handleStartThread = ({ value }: { value: number }) => {
        createMessage(value)
    }

    let threadOptions: { label: string | JSX.Element; value: number | undefined }[] = [
        {
            label: 'Contact administratie',
            value: orderId ? Number(orderId) : undefined
        }
    ]

    let subItems: any = []
    if (currentOrder && currentOrder.products) {
        subItems = currentOrder.products.map((product: OrderLine) => {
            return {
                label: (
                    <>
                        Contact over <strong>{product.name}</strong>
                    </>
                ),
                value: product.id
            }
        })
    }
    subItems = [...subItems, ...threadOptions]

    threadOptions = subItems

    return (
        <div className={'w-full flex-grow flex flex-col overflow-hidden  ' + className}>
            {canSearch && (
                <div className='p-3 bg-white rounded-tl-xl'>
                    <SearchInput placeholder='Zoek opdracht of locatie' onSearchChange={handleSearchChange} />
                </div>
            )}

            <div
                className={`border-btm overflow-y-auto w-full min-h-[656px] flex-grow bg-white ${
                    orderId ? '' : 'border-t'
                }`}
            >
                {loading ? (
                    <div className='w-full h-full flex items-center justify-center py-8'>
                        <DefaultLoader />
                    </div>
                ) : messageThreads.length ? (
                    messageThreads.map((thread: OrderMessageThread) => {
                        const isSelected = selectedThread === thread.id
                        return (
                            <div key={thread.id} ref={isSelected ? selectedRef : null}>
                                <MessageItem isSelected={isSelected} data={thread} onOrderDetail={onOrderDetail} />
                            </div>
                        )
                    })
                ) : (
                    <div className='w-full h-full flex items-center justify-center py-8'>Geen gesprekken gevonden</div>
                )}
            </div>

            <div className='bg-white w-full flex items-center justify-end px-3 py-4 rounded-bl-xl h-[76px] shrink-0'>
                {!onOrderDetail ? (
                    <IconButton iconName='plus' onClick={() => {}} disabled={true} />
                ) : (
                    actionButtons && (
                        <div
                            className='col-span-6 md:col-span-full lg:col-span-2 flex items-center self-end justify-end'
                            onClick={(e) => e.preventDefault()}
                        >
                            {actionButtons.indexOf('startThread') > -1 && (
                                <StartThreadButton
                                    disabled={loading}
                                    options={threadOptions}
                                    onChange={handleStartThread}
                                />
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    )
}

export default MessagesList
